import { createApi } from '@reduxjs/toolkit/query/react';

import {
  INotificationBounce,
  IUnsubscribedProducer,
  IUserNotification,
  NotificatioForm
} from '@/interfaces/notification';
import { IPaginationParams, IPaginationResponse } from '@/interfaces/pagination';
import { NOTIFICATION_CENTER_URL, API_V4_BASE_URL } from '@/settings';

import { post } from './api';
import { axiosBaseQuery } from './query';

export const saveUnsubscribeFeedback = (data: { reason: string; trackId: string }) => {
  return post({ baseURL: NOTIFICATION_CENTER_URL, url: '/unsubscribe/feedback', data });
};

export const notificationApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'notificationApi',
  tagTypes: ['list'],
  endpoints(build) {
    return {
      getUnsubscribedProducers: build.query<IUnsubscribedProducer[], void>({
        query: () => ({
          baseURL: API_V4_BASE_URL,
          formatResponse: response => response.data,
          url: '/unsubscribe/producers'
        })
      }),
      subscribeToProducer: build.mutation<void, number>({
        query: producerId => ({
          baseURL: NOTIFICATION_CENTER_URL,
          formatResponse: response => response.data.data,
          url: `/unsubscribe/producers/${producerId}`,
          method: 'DELETE'
        }),
        async onQueryStarted(producerId, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            const { updateQueryData } = notificationApi.util;

            dispatch(
              updateQueryData('getUnsubscribedProducers', undefined, draft => {
                return draft.filter(producer => producer.id !== producerId);
              })
            );
          } catch {}
        }
      }),
      getNotificationConfig: build.query<NotificatioForm, void>({
        query: () => ({
          baseURL: NOTIFICATION_CENTER_URL,
          formatResponse: response => response.data,
          url: '/config'
        })
      }),
      saveNotificationConfig: build.mutation<void, NotificatioForm>({
        query: data => ({
          baseURL: NOTIFICATION_CENTER_URL,
          method: 'PUT',
          url: '/config',
          data
        }),
        async onQueryStarted(data, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            const { updateQueryData } = notificationApi.util;

            dispatch(
              updateQueryData('getNotificationConfig', undefined, draft => {
                return Object.assign(draft, data);
              })
            );
          } catch {}
        }
      }),
      markAllAsRead: build.mutation<void, void>({
        invalidatesTags: ['list'],
        query: () => ({
          method: 'PUT',
          url: '/learner/notification-center/read-all'
        })
      }),
      toggleNotification: build.mutation<void, { id: string; read: boolean }>({
        invalidatesTags: ['list'],
        query: ({ id, read }) => ({
          method: 'PUT',
          url: `/learner/notification-center/${id}`,
          data: {
            read
          }
        })
      }),
      deleteNotification: build.mutation<void, string>({
        invalidatesTags: ['list'],
        query: notificationId => ({
          method: 'DELETE',
          url: `/learner/notification-center/${notificationId}`
        })
      }),
      getNotifications: build.query<IPaginationResponse<IUserNotification[]>, Partial<IPaginationParams>>({
        providesTags: ['list'],
        forceRefetch({ currentArg, previousArg }) {
          return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        merge: (currCache, newCache) => {
          currCache.total_pages = newCache.total_pages;
          currCache.page = newCache.page;
          currCache.links = newCache.links;

          if (newCache.page.page === 1) {
            currCache.data = newCache.data;
          } else {
            currCache.data.push(...newCache.data);
          }
        },
        query: params => ({
          url: '/learner/notifications',
          params
        })
      }),

      getBounce: build.query<INotificationBounce, void>({
        query: () => ({
          baseURL: NOTIFICATION_CENTER_URL,
          formatResponse: response => response.data,
          url: '/bounce'
        })
      }),

      deleteBounce: build.mutation<void, void>({
        query: () => ({
          baseURL: NOTIFICATION_CENTER_URL,
          method: 'DELETE',
          url: '/bounce'
        }),
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          await queryFulfilled;
          dispatch(
            notificationApi.util.updateQueryData('getBounce', undefined, draft => {
              Object.assign(draft, { exists: false });
            })
          );
        }
      })
    };
  }
});

export const {
  useMarkAllAsReadMutation,
  useGetNotificationsQuery,
  useDeleteNotificationMutation,
  useToggleNotificationMutation,
  useGetNotificationConfigQuery,
  useSaveNotificationConfigMutation,
  useGetUnsubscribedProducersQuery,
  useSubscribeToProducerMutation,
  useGetBounceQuery,
  useDeleteBounceMutation
} = notificationApi;
